import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { IssuedKitItem, Station } from '@hcc/types';
import { api } from '../lib/api';

/**
 * The drawer's working state, as stored server-side between visits. Only
 * what a second desk needs to carry on: what was ticked, what was typed,
 * and which confirm rows were resolved.
 *
 * Condition photos are deliberately absent. They are multi-megabyte base64
 * JPEGs held in the drawer's memory, and autosaving them on every keystroke
 * would push tens of megabytes per person through the box on a mobilisation
 * day. A photo taken but not signed for is lost, and the drawer says so.
 */
export interface KitDraftPayload {
  items: IssuedKitItem[];
  /** Category shadow map for tracked-asset rows (see useKitIssueItems). */
  asset_category_by_id: Record<string, string | null>;
  /**
   * Confirm-row edits keyed by asset. Rehydrated against the live
   * pre-assigned list on load, so an asset that has since been issued
   * elsewhere simply drops out rather than reappearing as outstanding.
   */
  confirm: Array<{
    asset_id: string;
    outcome: 'confirmed' | 'not_present' | null;
    serial?: string;
    registration?: string;
    last_service_date?: string;
    attributes?: Record<string, string>;
  }>;
  notes: string;
  /**
   * Idempotency key for the eventual KitIssued, carried in the draft so
   * every desk that picks this up submits under the same key: if two of
   * them sign at once the server dedupes instead of double-issuing.
   */
  client_event_id: string;
}

export interface KitDraftEnvelope {
  draft: KitDraftPayload;
  version: number;
  updated_at: string;
  updated_by: string | null;
  updated_by_name: string | null;
}

/** What the drawer's state hooks seed themselves from on open. */
export interface KitDraftSeed {
  /** False until the saved draft has been fetched; nothing seeds before then. */
  ready: boolean;
  /** Bumped to force a re-seed after loading someone else's newer draft. */
  nonce: number;
  items?: IssuedKitItem[];
  assetCategoryById?: Record<string, string | null>;
  confirm?: KitDraftPayload['confirm'];
}

export function kitDraftKey(personId: string, station: Station) {
  return ['kit-draft', personId, station] as const;
}

/**
 * The saved draft for this person at this station, or null. Never refetched
 * on its own: the drawer seeds from it once and then owns the state, so a
 * background refetch mid-edit would be at best wasted and at worst confusing.
 */
export function useKitDraftQuery(personId: string, station: Station) {
  return useQuery({
    queryKey: kitDraftKey(personId, station),
    queryFn: () => api<{ draft: KitDraftEnvelope | null }>(
      `/api/kit-drafts/people/${personId}?station=${station}`,
    ),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    gcTime: 0,
  });
}

export interface SaveKitDraftInput {
  station: Station;
  draft: KitDraftPayload;
  /** Omitted for the first save; the loaded version thereafter. */
  version?: number;
}

/** Raised when the server refused a save because someone else got there first. */
export interface KitDraftConflict {
  message: string;
  current: KitDraftEnvelope;
}

export function useSaveKitDraft(personId: string) {
  return useMutation({
    mutationFn: (input: SaveKitDraftInput) =>
      api<{ draft: KitDraftEnvelope }>(`/api/kit-drafts/people/${personId}`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
  });
}

export function useDiscardKitDraft(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (station: Station) =>
      api<void>(`/api/kit-drafts/people/${personId}?station=${station}`, {
        method: 'DELETE',
      }),
    onSuccess: (_data, station) => {
      qc.removeQueries({ queryKey: kitDraftKey(personId, station) });
    },
  });
}
